var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import OptionMasterPanelTemplate from '@front/templates/OptionMasterPanelTemplate.html';
import { IVCalculationPrice, getIVCalculationPriceKey } from '@shared/commons/cache/OptionMaster/OptionTrader/IVCalculationPrice';
import { OptionTrader } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionTrader';
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { Resources } from '@shared/localizations/Resources';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
import { Level1ItemType } from '@shared/utils/Enums/Level1ItemType';
import { OptionSerializer } from '@shared/utils/Instruments/OptionSerializer';
import { OptionType } from '@shared/utils/Instruments/OptionType';
import { ActivationDirection } from '../../elements/ResizeSplitter';
import { PanelSettingsScreen } from '../../screen/PanelSettingsScreen';
import { PropertySetupScreen } from '../../screen/PropertySetupScreen';
import { PanelNames } from '../../UtilsClasses/FactoryConstants';
import { IsAllowedPanel } from '../../WorkSpace/WorkSpaceUtils';
import { ApplicationPanel } from '../ApplicationPanel';
export class OptionMasterPanel extends ApplicationPanel {
    constructor() {
        super();
        this._tickCounter = 0;
        this._isInitialized = false;
        this.Name = 'OptionMasterPanel';
        this.headerLocaleKey = 'panel.optionMaster';
    }
    getType() {
        return PanelNames.OptionMasterPanel;
    }
    oninit() {
        super.oninit();
        this._optionTrader = new OptionTrader();
        super.observe('instrument', this.onInstrumentChanged);
        super.observe('account', this.onAccountChanged);
        super.on('onInfoButtonClick', this.onInfoButtonClicked);
        super.on('onSettingsButtonClick', this.onSettingsButtonClicked);
        super.on('onSplitterResize', this.onSplitterResized);
        super.on('onSplitterActivate', this.onSplitterActivate);
        super.on('onSplitterDeactivate', this.onSplitterDeactivate);
        super.on('onActivePanelChange', this.onActivePanelChanged);
        super.on('onCollapsedChange', this.onCollapsedChanged);
        super.on('onVisibleTopPanelsCountChange', this.onVisibleTopPanelsCountChanged);
        super.on('onVisibleBottomPanelsCountChange', this.onVisibleBottomPanelsCountChanged);
    }
    onteardown() {
        super.onteardown();
    }
    oncomplete() {
        super.oncomplete();
        this.initializeVariables();
        this.applyTheme();
        if (!this.deserializeDelayedData()) {
            void this.setDefaultInstrumentAsync();
        }
        this.layoutTable();
        this.localize();
        this.repopulate();
    }
    dispose() {
        this._optionTrader.dispose();
        this._optionAnalyzerPanel.dispose();
        this._optionVolatilityLabPanel.dispose();
        this._optionPositionsPanel.dispose();
        this._optionOrdersPanel.dispose();
        this._optionPaperPositionsPanel.dispose();
        super.dispose();
    }
    localize() {
        super.localize();
        if (!this._isInitialized) {
            return;
        }
        const containerControls = this.findAllComponents('panelsContainerControl');
        if (isValidArray(containerControls)) {
            for (let i = 0; i < containerControls.length; i++) {
                containerControls[i].localize();
            }
        }
        this._optionChainPanel.localize();
        this._optionAnalyzerPanel.localize();
        this._optionVolatilityLabPanel.localize();
        this._optionPositionsPanel.localize();
        this._optionOrdersPanel.localize();
        this._optionPaperPositionsPanel.localize();
    }
    applyTheme() {
        if (!this._isInitialized) {
            return;
        }
        super.applyTheme();
        this._optionChainPanel.applyTheme();
        this._optionAnalyzerPanel.applyTheme();
        this._optionVolatilityLabPanel.applyTheme();
    }
    applyProperties(properties) {
        super.applyProperties(properties);
        this._optionChainPanel.applyProperties(properties);
        this._optionAnalyzerPanel.applyProperties(properties);
        this._optionVolatilityLabPanel.applyProperties(properties);
    }
    themeChange() {
        if (!this._isInitialized) {
            return;
        }
        this._optionChainPanel.callThemeChange();
        this._optionAnalyzerPanel.callThemeChange();
        this._optionVolatilityLabPanel.callThemeChange();
        this._optionPositionsPanel.callThemeChange();
        this._optionOrdersPanel.callThemeChange();
        this._optionPaperPositionsPanel.callThemeChange();
    }
    layoutTable() {
        if (!this._isInitialized) {
            return;
        }
        this._optionChainPanel.layoutTable();
        this._optionAnalyzerPanel.layoutTable();
        this._optionVolatilityLabPanel.layoutTable();
        this._optionPositionsPanel.layoutTable();
        this._optionOrdersPanel.layoutTable();
        this._optionPaperPositionsPanel.layoutTable();
    }
    setSize(w, h) {
        super.setSize(w, h);
        this.layoutTable();
    }
    TickAsync() {
        if (!this._isInitialized) {
            return;
        }
        if (!this.get('visible')) {
            return;
        }
        this._tickCounter++;
        if (this._tickCounter % 12 === 0) {
            // 60fps / 5 = 12
            this._optionChainPanel.updateData();
            this._optionPaperPositionsPanel.updateData();
            this._optionAnalyzerPanel.updateChart();
            this._optionVolatilityLabPanel.updateChart();
            this._tickCounter = 0;
        }
        this._optionChainPanel.TickAsync();
        this._optionPositionsPanel.TickAsync();
        this._optionOrdersPanel.TickAsync();
        this._optionPaperPositionsPanel.TickAsync();
        this._optionAnalyzerPanel.TickAsync();
        this._optionVolatilityLabPanel.TickAsync();
    }
    populate() {
        this.repopulate();
    }
    repopulate() {
        var _a;
        if (!this._isInitialized) {
            return;
        }
        this.repopulatePanels([this._optionPositionsPanel, this._optionOrdersPanel, this._optionPaperPositionsPanel]);
        this._optionTrader.account = super.get('account');
        this._optionTrader.fakeOption = super.get('instrument');
        this._optionChainPanel.repopulate();
        this.repopulatePanels([this._optionAnalyzerPanel, this._optionVolatilityLabPanel]);
        void super.set('underlier', (_a = this._optionTrader.fakeOption) === null || _a === void 0 ? void 0 : _a.ForwardBaseInstrument);
    }
    updateSettings() {
        super.updateSettings();
        if (!this._isInitialized) {
            return;
        }
        this._optionChainPanel.updateSettings();
        this._optionAnalyzerPanel.updateSettings();
        this._optionVolatilityLabPanel.updateSettings();
        this._optionPositionsPanel.updateSettings();
        this._optionOrdersPanel.updateSettings();
        this._optionPaperPositionsPanel.updateSettings();
        this.layoutTable();
    }
    Properties() {
        const properties = super.Properties();
        let index = 0;
        let dp = new DynProperty('property.displayNonStandartContracts', this._optionTrader.isDisplayNonStandartContracts, DynProperty.BOOLEAN, DynProperty.GENERAL_GROUP);
        dp.tooltip = Resources.getResource('property.displayNonStandartContracts.tooltip');
        dp.sortIndex = index++;
        properties.push(dp);
        const separatorGroup = '#0#' + Resources.getResource('property.Calculation');
        dp = new DynProperty('property.InterestRate', this._optionTrader.interestRate * 100, DynProperty.DOUBLE, DynProperty.GENERAL_GROUP);
        dp.sortIndex = index++;
        dp.minimalValue = 0;
        dp.maximalValue = 999999999;
        dp.increment = 0.01;
        dp.decimalPlaces = 2;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        dp = new DynProperty('property.IVCalculationPrice', this._optionTrader.ivCalculationPrice, DynProperty.COMBOBOX, DynProperty.GENERAL_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(IVCalculationPrice, getIVCalculationPriceKey);
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        properties.push(dp);
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument)) {
            const optionSerializeData = OptionSerializer.createOptionSerializeData(instrument);
            properties.push(new DynProperty('symbol', OptionSerializer.serialize(optionSerializeData), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        properties.push(...this._optionChainPanel.Properties());
        if (IsAllowedPanel(this._optionAnalyzerPanel)) {
            properties.push(...this._optionAnalyzerPanel.Properties());
        }
        if (IsAllowedPanel(this._optionVolatilityLabPanel)) {
            properties.push(...this._optionVolatilityLabPanel.Properties());
        }
        const level1Panel = this.findComponent('terceraLevel1Panel');
        properties.push(...level1Panel.properties());
        return properties;
    }
    onClosePropertiesScreen() {
        void this.set('isSettingsOpened', false);
    }
    callBack(properties) {
        super.callBack(properties);
        const group = DynProperty.GENERAL_GROUP;
        let dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.InterestRate');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.interestRate = dp.value / 100;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.IVCalculationPrice');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.ivCalculationPrice = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, DynProperty.HIDDEN_GROUP, 'symbol');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            const instrument = this.get('instrument');
            const propertiesSerializedData = OptionSerializer.deserialize(dp.value);
            const currentInstrumentSerializedData = OptionSerializer.createOptionSerializeData(instrument);
            if (!propertiesSerializedData.equals(currentInstrumentSerializedData)) {
                void this._optionTrader
                    .deserializeOptionAsync(propertiesSerializedData.optionId, propertiesSerializedData.optionRoute, propertiesSerializedData.contractTradableId)
                    .then((ins) => {
                    if (ins != null) {
                        void super.set('instrument', ins);
                    }
                    else {
                        void this.setDefaultInstrumentAsync();
                    }
                });
            }
            else {
                void this.setDefaultInstrumentAsync();
            }
        }
        else {
            void this.setDefaultInstrumentAsync();
        }
        this._optionChainPanel.callCallback(properties);
        this._optionAnalyzerPanel.callCallback(properties);
        this._optionVolatilityLabPanel.callCallback(properties);
        const level1Panel = this.findComponent('terceraLevel1Panel');
        level1Panel.callBack(properties);
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.displayNonStandartContracts');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.isDisplayNonStandartContracts = dp.value;
        }
    }
    closeAddidionalScreens(mainComponent) {
        const mainWindowControls = mainComponent.Controls;
        const propertySetupScreens = Object.values(mainWindowControls).filter((control) => control instanceof PropertySetupScreen);
        propertySetupScreens.forEach((screen) => screen.close());
    }
    onInstrumentChanged(instrument, prevInstrument) {
        if (!this._isInitialized) {
            return;
        }
        if (instrument === prevInstrument) {
            return;
        }
        this._optionTrader.fakeOption = instrument;
        void super.set('underlier', instrument === null || instrument === void 0 ? void 0 : instrument.ForwardBaseInstrument);
    }
    onAccountChanged(account) {
        if (!this._isInitialized) {
            return;
        }
        this.accountLink_Out(false, account);
        this._optionTrader.account = account;
    }
    onInfoButtonClicked() {
        const _super = Object.create(null, {
            get: { get: () => super.get },
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const isShowPortfolio = _super.get.call(this, 'isShowPortfolio');
            yield _super.set.call(this, 'isShowPortfolio', !isShowPortfolio);
            this.layoutTable();
        });
    }
    onSettingsButtonClicked() {
        PanelSettingsScreen.EditProperties(this, null, Resources.getResource('panel.optionMaster.settingsHeader'));
        void this.set('isSettingsOpened', true);
    }
    onSplitterResized() {
        this.layoutTable();
    }
    onSplitterActivate() {
        void super.set('isCollapsedBottom', false);
    }
    onSplitterDeactivate() {
        void super.set('isCollapsedBottom', true);
    }
    onActivePanelChanged() {
        this.layoutTable();
    }
    onCollapsedChanged(context, isCollapsed) {
        const _super = Object.create(null, {
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            void _super.set.call(this, 'isCollapsedBottom', isCollapsed);
        });
    }
    onVisibleTopPanelsCountChanged(context, visiblePanelsCount) {
        void super.set('isVisibleTopTabs', visiblePanelsCount > 1);
    }
    onVisibleBottomPanelsCountChanged(context, visiblePanelsCount) {
        void super.set('isVisibleBottom', visiblePanelsCount > 0);
    }
    // #region Heper methods
    initializeVariables() {
        this._optionPortfolioControl = super.findComponent('optionPortfolioControl');
        this._optionChainPanel = super.findComponent('optionChainPanel');
        this._optionAnalyzerPanel = super.findComponent('optionAnalyzerPanel');
        this._optionVolatilityLabPanel = super.findComponent('optionVolatilityLabPanel');
        this._optionPositionsPanel = super.findComponent('optionPositionsPanel');
        this._optionOrdersPanel = super.findComponent('optionOrdersPanel');
        this._optionPaperPositionsPanel = super.findComponent('optionPaperPositionsPanel');
        this._optionPortfolioControl.setOptionTrader(this._optionTrader);
        this._optionChainPanel.setOptionTrader(this._optionTrader);
        this._optionAnalyzerPanel.setOptionTrader(this._optionTrader);
        this._optionVolatilityLabPanel.setOptionTrader(this._optionTrader);
        this._optionPositionsPanel.setOptionTrader(this._optionTrader);
        this._optionOrdersPanel.setOptionTrader(this._optionTrader);
        this._optionPaperPositionsPanel.setOptionTrader(this._optionTrader);
        this._isInitialized = true;
    }
    repopulatePanels(panels) {
        for (let i = 0; i < panels.length; i++) {
            const panel = panels[i];
            const forbiddenPanel = !IsAllowedPanel(panel);
            void panel.set('forbiddenPanel', forbiddenPanel);
            if (!forbiddenPanel) {
                panel.repopulate();
            }
        }
    }
    setDefaultInstrumentAsync() {
        const _super = Object.create(null, {
            set: { get: () => super.set }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const defaultOption = DataCache.getDefaultInstrument(true);
            if (!isNullOrUndefined(defaultOption) && defaultOption.optionType === OptionType.Fake) {
                const option = yield this._optionTrader.getOptionAsync(defaultOption);
                void _super.set.call(this, 'instrument', option);
            }
            else {
                void _super.set.call(this, 'instrument', defaultOption);
            }
        });
    }
}
ApplicationPanel.extendWith(OptionMasterPanel, {
    data: function () {
        return {
            showFullscreenModeButton: true,
            isShowToolbar: true,
            isShowPortfolio: false,
            instrument: null,
            account: null,
            underlier: null,
            level1Types: [
                Level1ItemType.Last,
                Level1ItemType.Ask,
                Level1ItemType.Bid,
                Level1ItemType.Change,
                Level1ItemType.Volume,
                Level1ItemType.Open,
                Level1ItemType.High,
                Level1ItemType.Low,
                Level1ItemType.PrevClose
            ],
            isVisibleTopTabs: false,
            isVisibleBottom: false,
            isCollapsedBottom: false,
            isSettingsOpened: false,
            splitterActivationDirection: ActivationDirection.BOTTOM
        };
    },
    partials: {
        bodyPartial: OptionMasterPanelTemplate
    }
});
